import { useState } from "react";
import { motion } from "framer-motion";
import { FormField } from "./form-field";
import { Select } from "./select";
import { Button } from "./button";

type Option = { value: string; label: string };

type DropdownMultiSelectProps = {
  id: string;
  label: string;
  helpText?: string;
  values: string[];
  options: Option[];
  addLabel: string;
  emptyLabel: string;
  disabled?: boolean;
  onChange: (next: string[]) => void;
};

function unique(values: string[]) {
  return [...new Set(values)];
}

export function DropdownMultiSelect({
  id,
  label,
  helpText,
  values,
  options,
  addLabel,
  emptyLabel,
  disabled = false,
  onChange,
}: DropdownMultiSelectProps) {
  const [selectedValue, setSelectedValue] = useState("");
  const available = options.filter((option) => !values.includes(option.value));
  const visibleSelectedValue = available.some((option) => option.value === selectedValue)
    ? selectedValue
    : "";

  const addSelected = () => {
    if (!visibleSelectedValue) return;
    onChange(unique([...values, visibleSelectedValue]));
    setSelectedValue("");
  };

  return (
    <FormField label={label} htmlFor={id} helpText={helpText}>
      <motion.div
        className="cf-grid"
        layout
        initial={false}
        transition={{ layout: { duration: 0.22, ease: "easeOut" } }}
      >
        <div
          style={{
            display: "grid",
            gap: "var(--cf-space-sm)",
            gridTemplateColumns: "minmax(0, 1fr) auto",
          }}
        >
          <Select
            id={id}
            value={visibleSelectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
            disabled={disabled}
          >
            <option value="">{`Choose ${label.toLowerCase()}`}</option>
            {available.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Button type="button" onClick={addSelected} disabled={disabled || !visibleSelectedValue}>
            {addLabel}
          </Button>
        </div>
        <div className="cf-tag-select-selected">
          {values.length === 0 ? <p className="cf-help">{emptyLabel}</p> : null}
          {values.map((value) => {
            const option = options.find((item) => item.value === value);
            const labelText = option?.label ?? value;
            return (
              <Button
                key={value}
                type="button"
                size="sm"
                variant="solid"
                tone="neutral"
                disabled={disabled}
                onClick={() => onChange(values.filter((item) => item !== value))}
              >
                {labelText} ×
              </Button>
            );
          })}
        </div>
      </motion.div>
    </FormField>
  );
}
